import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipForward, SkipBack, X, RotateCcw, RotateCw, Share2, Radio } from "lucide-react";
import {
  registerPlayingChangeCallback,
  registerProgressCallback,
  togglePauseGlobal,
  playNext,
  playPrev,
  seekGlobal,
  stopAllMedia,
  setRadioMode,
} from "../hooks/useAudioPlayer";
import { toast } from "sonner";

function fmt(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

export default function MiniPlayer() {
  const [current, setCurrent] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragTime, setDragTime] = useState(null); // visual-only when dragging
  const [radio, setRadio] = useState(() => localStorage.getItem("ap_radio_mode") !== "off");
  const seekRef = useRef(null);

  // Apply radio mode on mount and whenever it changes
  useEffect(() => {
    setRadioMode(radio);
    localStorage.setItem("ap_radio_mode", radio ? "on" : "off");
  }, [radio]);

  useEffect(() => {
    return registerPlayingChangeCallback((id, music) => {
      if (music) {
        setCurrent(music);
        setIsPaused(false);
        setTime(0);
      } else {
        setIsPaused(true);
      }
    });
  }, []);

  useEffect(() => {
    return registerProgressCallback((cur, dur) => {
      if (!dragging) setTime(cur);
      if (dur && dur !== duration) setDuration(dur);
    });
  }, [duration, dragging]);

  // === Drag-to-seek handling ===
  const computeSeek = (clientX) => {
    const bar = seekRef.current;
    if (!bar || !duration) return null;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pct * duration;
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const t = computeSeek(clientX);
      if (t !== null) setDragTime(t);
    };
    const onUp = (e) => {
      const clientX = (e.changedTouches && e.changedTouches[0])
        ? e.changedTouches[0].clientX
        : (e.clientX ?? null);
      if (clientX !== null) {
        const t = computeSeek(clientX);
        if (t !== null) {
          seekGlobal(t);
          setTime(t);
        }
      } else if (dragTime !== null) {
        seekGlobal(dragTime);
        setTime(dragTime);
      }
      setDragging(false);
      setDragTime(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, duration, dragTime]);

  if (!current) return null;

  const handleClose = () => {
    stopAllMedia();
    setCurrent(null);
  };

  const handleBarMouseDown = (e) => {
    setDragging(true);
    const t = computeSeek(e.clientX);
    if (t !== null) setDragTime(t);
  };
  const handleBarTouchStart = (e) => {
    setDragging(true);
    const t = computeSeek(e.touches[0].clientX);
    if (t !== null) setDragTime(t);
  };

  const skip = (delta) => {
    const newT = Math.max(0, Math.min(duration || 0, time + delta));
    seekGlobal(newT);
    setTime(newT);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/?play=${current.id}`;
    const shareData = {
      title: `${current.title} - ${current.artist_name}`,
      text: `Ouça "${current.title}" de ${current.artist_name} no Arrocha Play`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado para a área de transferência!");
    } catch (err) {
      // User cancelled native share - try clipboard fallback
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado!");
      } catch {
        toast.error("Não foi possível compartilhar");
      }
    }
  };

  const displayTime = dragging && dragTime !== null ? dragTime : time;
  const pct = duration > 0 ? (displayTime / duration) * 100 : 0;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-white/10 shadow-2xl shadow-black/60"
      data-testid="mini-player"
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-4 pt-2 sm:pt-3 pb-2">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Track info */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            {current.artist_image ? (
              <img
                src={current.artist_image}
                alt={current.artist_name}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] sm:text-xs text-[#FF007F] font-bold uppercase tracking-wider mb-0.5 truncate flex items-center gap-1">
                {radio ? <><Radio size={11} className="inline" /> <span className="hidden xs:inline">RÁDIO ARROCHAPLAY · </span>TOCANDO</> : <>▶ Tocando</>}
              </p>
              <p className="text-white text-xs sm:text-sm font-medium truncate" data-testid="mini-player-title">
                {current.title}
              </p>
              <p className="text-zinc-500 text-[11px] sm:text-xs truncate">{current.artist_name}</p>
            </div>
          </div>

          {/* Controls — em mobile mostra tudo, só menor */}
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            <button
              onClick={playPrev}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all"
              title="Faixa anterior"
              data-testid="mini-player-prev"
            >
              <SkipBack size={16} />
            </button>
            <button
              onClick={() => skip(-10)}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all"
              title="Voltar 10 segundos"
              data-testid="mini-player-skip-back"
            >
              <RotateCcw size={14} />
            </button>
            <button
              onClick={() => {
                togglePauseGlobal();
                setIsPaused((prev) => !prev);
              }}
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#FF007F] hover:bg-[#FF3399] text-white flex items-center justify-center transition-all"
              title={isPaused ? "Tocar" : "Pausar"}
              data-testid="mini-player-toggle"
            >
              {isPaused ? <Play size={18} className="ml-0.5" fill="white" /> : <Pause size={18} fill="white" />}
            </button>
            <button
              onClick={() => skip(10)}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all"
              title="Avançar 10 segundos"
              data-testid="mini-player-skip-fwd"
            >
              <RotateCw size={14} />
            </button>
            <button
              onClick={playNext}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all"
              title="Próxima faixa"
              data-testid="mini-player-next"
            >
              <SkipForward size={16} />
            </button>
            <button
              onClick={handleShare}
              className="hidden sm:flex w-9 h-9 rounded-full text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 items-center justify-center transition-all ml-1"
              title="Compartilhar"
              data-testid="mini-player-share"
            >
              <Share2 size={16} />
            </button>
            <button
              onClick={() => setRadio((r) => !r)}
              className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all ${
                radio ? "text-[#FF007F] bg-[#FF007F]/10" : "text-zinc-500 hover:text-white hover:bg-white/10"
              }`}
              title={radio ? "Rádio ligada" : "Rádio desligada"}
              data-testid="mini-player-radio-toggle"
            >
              <Radio size={14} />
            </button>
            <button
              onClick={handleClose}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full text-zinc-500 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all"
              title="Fechar"
              data-testid="mini-player-close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Progress bar (drag-to-seek) */}
        <div className="flex items-center gap-3 mt-2 select-none">
          <span className="text-[11px] text-zinc-500 tabular-nums w-9 text-right" data-testid="mini-player-time">
            {fmt(displayTime)}
          </span>
          <div
            ref={seekRef}
            onMouseDown={handleBarMouseDown}
            onTouchStart={handleBarTouchStart}
            className={`flex-1 h-1.5 bg-white/10 rounded-full cursor-pointer group relative ${dragging ? "h-2" : ""}`}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={Math.max(1, Math.floor(duration))}
            aria-valuenow={Math.floor(displayTime)}
            data-testid="mini-player-seek"
          >
            <div
              className="absolute inset-y-0 left-0 bg-[#FF007F] rounded-full pointer-events-none"
              style={{ width: `${pct}%`, transition: dragging ? "none" : "width 0.1s linear" }}
            />
            {/* Always-visible thumb */}
            <div
              className={`absolute top-1/2 w-3.5 h-3.5 bg-[#FF007F] rounded-full shadow-lg pointer-events-none transition-opacity ${
                dragging ? "opacity-100 scale-125" : "opacity-0 group-hover:opacity-100"
              }`}
              style={{ left: `calc(${pct}% - 7px)`, transform: "translateY(-50%)" }}
            />
          </div>
          <span className="text-[11px] text-zinc-500 tabular-nums w-9" data-testid="mini-player-duration">
            {fmt(duration)}
          </span>
        </div>
      </div>
    </div>
  );
}
